import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { fmtDateBoth } from '../../utils/dates';
import { NOTE_STATUS_AR, NOTE_STATUS_BADGE } from './BookStatus';

const emptyDraft = { body: '', location: '', page_no: '' };

/**
 * الانتقالات المسموحة لكل حالة بند، ومن يملكها.
 *
 * أزرار صريحة لا دورة نقر: حالة البند نطاقٌ قابل للتدقيق (المعلّق يُرفق
 * بالملف النهائي)، فالنقر الذي «يدوّر» الحالات يُفسدها بضغطة زائدة.
 */
const TRANSITIONS = [
  { to: 'revised', label: '↻ عُدِّل', owner: 'summarize', from: ['new', 'pending'] },
  { to: 'accepted', label: '✓ مقبول', owner: 'review', from: ['new', 'revised', 'pending'] },
  { to: 'pending', label: '⏸ معلّق', owner: 'review', from: ['new', 'revised', 'accepted'] },
  { to: 'new', label: '↩ إعادة فتح', owner: 'review', from: ['revised', 'accepted', 'pending'] },
];

export default function NoteItemsPanel({ summary, notes, rounds, abilities, onChange }) {
  const { api } = useAuth();
  const toast = useToast();
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [openRounds, setOpenRounds] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');

  const canAdd = (abilities.manage || abilities.review) && summary.status === 'under_review';
  const pendingCount = notes.filter(n => n.status === 'pending').length;
  const newCount = notes.filter(n => n.status === 'new').length;

  const grouped = useMemo(() => {
    const byRound = new Map();
    notes.forEach(note => {
      const key = note.round_id ?? 0;
      if (!byRound.has(key)) byRound.set(key, []);
      byRound.get(key).push(note);
    });
    return [...byRound.entries()]
      .map(([roundId, items]) => ({
        roundId,
        roundNo: rounds.find(r => r.id === roundId)?.round_no ?? 0,
        items,
      }))
      .sort((a, b) => b.roundNo - a.roundNo);
  }, [notes, rounds]);

  // أحدث جولة مفتوحة والأقدم مطويّة، حتى يتدخّل المستخدم
  const isOpen = (roundId) => (openRounds ? openRounds.includes(roundId) : roundId === grouped[0]?.roundId);

  const toggleRound = (roundId) => {
    const current = openRounds ?? (grouped[0] ? [grouped[0].roundId] : []);
    setOpenRounds(current.includes(roundId) ? current.filter(r => r !== roundId) : [...current, roundId]);
  };

  const add = async () => {
    if (!draft.body.trim()) return;
    setSaving(true);
    try {
      await api(`/book-summaries/${summary.id}/notes`, {
        method: 'POST',
        body: JSON.stringify({
          body: draft.body.trim(),
          location: draft.location.trim() || null,
          page_no: draft.page_no ? Number(draft.page_no) : null,
        }),
      });
      setDraft(emptyDraft);
      toast.success('أُضيف البند');
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (note, to, reply) => {
    try {
      await api(`/book-summary-notes/${note.id}/status`, {
        method: 'PUT',
        body: JSON.stringify(reply ? { status: to, reply } : { status: to }),
      });
      setReplyFor(null);
      setReplyText('');
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openReply = (note) => {
    setReplyFor(note.id);
    setReplyText(note.researcher_reply || '');
  };

  const remove = async (note) => {
    if (!window.confirm(`حذف البند ${note.seq}؟`)) return;
    try {
      await api(`/book-summary-notes/${note.id}`, { method: 'DELETE' });
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const allowed = (note) => TRANSITIONS.filter(t =>
    t.from.includes(note.status) && (abilities.manage || abilities.approve || abilities[t.owner])
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="card-title" style={{ margin: 0 }}>📋 بنود الملاحظات</div>
        <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>{notes.length} بنداً</span>
        {newCount > 0 && <span className="badge badge-pending">{newCount} لم يُنظر فيها</span>}
        {pendingCount > 0 && <span className="badge badge-revision">{pendingCount} معلّق</span>}
      </div>

      {pendingCount > 0 && summary.status !== 'approved' && (
        <div
          style={{
            background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8,
            padding: 10, marginBottom: 12, fontSize: 13, color: 'var(--text-muted)',
          }}
        >
          البنود المعلّقة تُرفق مع الملف النهائي — ارفع ملفها في «ملف الملاحظات المعلّقة» قبل الاعتماد.
        </div>
      )}

      {canAdd && (
        <div className="form-row" style={{ alignItems: 'flex-end', marginBottom: 14 }}>
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">نصّ الملاحظة</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={draft.body}
              onChange={e => setDraft({ ...draft, body: e.target.value })}
              placeholder="ما الملاحظة على هذا الموضع؟"
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">الموضع</label>
            <input
              className="form-input"
              value={draft.location}
              onChange={e => setDraft({ ...draft, location: e.target.value })}
              placeholder="الفصل الثاني"
            />
          </div>
          <div className="form-group" style={{ flex: '0 0 90px' }}>
            <label className="form-label">الصفحة</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={draft.page_no}
              onChange={e => setDraft({ ...draft, page_no: e.target.value })}
            />
          </div>
          <div className="form-group">
            <button className="btn btn-primary" onClick={add} disabled={saving || !draft.body.trim()}>
              {saving ? '...' : 'إضافة بند'}
            </button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="empty" style={{ padding: 20 }}>
          <div className="empty-icon">📋</div>
          لا توجد بنود ملاحظات
        </div>
      ) : (
        grouped.map(group => (
          <div key={group.roundId} style={{ marginBottom: 12 }}>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => toggleRound(group.roundId)}
              style={{ marginBottom: 8 }}
            >
              {isOpen(group.roundId) ? '▾' : '▸'} الجولة {group.roundNo || '—'} ({group.items.length})
            </button>

            {isOpen(group.roundId) && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>#</th>
                      <th>الملاحظة</th>
                      <th style={{ width: 130 }}>الموضع</th>
                      <th style={{ width: 100 }}>الحالة</th>
                      <th style={{ width: 220 }}>الإجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map(note => (
                      <tr key={note.id}>
                        <td style={{ fontWeight: 600 }}>{note.seq}</td>
                        <td>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{note.body}</div>
                          {note.researcher_reply && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                              ↳ ردّ الباحث: {note.researcher_reply}
                            </div>
                          )}
                          {note.author?.name && (
                            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
                              {note.author.name} · {fmtDateBoth(note.created_at)}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {note.location || '—'}
                          {note.page_no ? ` (ص ${note.page_no})` : ''}
                        </td>
                        <td>
                          <span className={`badge badge-${NOTE_STATUS_BADGE[note.status]}`}>
                            {NOTE_STATUS_AR[note.status]}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {allowed(note).map(t => (
                              <button
                                key={t.to}
                                className="btn btn-sm btn-outline"
                                onClick={() => {
                                  if (t.to === 'revised') openReply(note);
                                  else setStatus(note, t.to);
                                }}
                              >
                                {t.label}
                              </button>
                            ))}
                            {note.status === 'new' && (abilities.manage || abilities.review) && (
                              <button className="btn-icon" onClick={() => remove(note)} title="حذف">✕</button>
                            )}
                          </div>

                          {replyFor === note.id && (
                            <div style={{ marginTop: 6 }}>
                              <textarea
                                className="form-textarea"
                                rows={2}
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                placeholder="بماذا عُدِّل؟ (اختياري)"
                              />
                              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setStatus(note, 'revised', replyText.trim() || null)}
                                >
                                  حفظ
                                </button>
                                <button className="btn btn-sm btn-outline" onClick={() => setReplyFor(null)}>
                                  إلغاء
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
